import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * A label that appears on hover and on keyboard focus.
 *
 * CSS only, and it never carries anything you cannot get another way: a
 * tooltip is unreachable on a touch screen, so it explains, it does not inform.
 * The text is wired in through `aria-describedby`'s cheaper cousin — a live
 * region is overkill for a hint, so the trigger keeps its own accessible name.
 */
export function Tooltip({
  label,
  side = "top",
  children,
  className,
}: {
  /** Nothing to say means no tooltip at all, rather than an empty box. */
  label: ReactNode;
  side?: "top" | "bottom";
  children: ReactNode;
  className?: string;
}) {
  if (!label) return <>{children}</>;
  return (
    <span className={cn("group/tip relative inline-flex", className)}>
      {children}
      <span
        role="tooltip"
        className={cn(
          "text-footnote pointer-events-none absolute left-1/2 z-50 -translate-x-1/2 whitespace-nowrap",
          "rounded-md px-2 py-1 font-medium",
          "bg-ink-900 dark:bg-ink-100 dark:text-ink-900 text-white",
          "opacity-0 transition-opacity duration-[var(--duration-fast)]",
          "group-focus-within/tip:opacity-100 group-hover/tip:opacity-100",
          side === "top" ? "bottom-full mb-1.5" : "top-full mt-1.5",
        )}
      >
        {label}
      </span>
    </span>
  );
}
